import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import Image from 'next/image';
import { Facebook } from 'clad-ui/icons';
import theme from '@clad-ui/theme';
import config from '~/config';
import { makeId, parseJwt, checkIfWebview } from '~/helpers/util';
import { useAuthorize, useLoginViaFacebook, useLoginViaApple, useLoginViaGoogle } from '~/apis/login';
import { removeCookie } from '~/helpers/api/Cookie';
import { asyncGTMClickEvent, gtmCustomDemesion, genderToGA4Data } from '~/helpers/tagging/gtm';
import fingerprint from '~/helpers/fingerprint';
import fingerprintV2 from '~/helpers/fingerprintV2';
import type { Error } from '~/apis/useRequest';
const {
  colors
} = theme;
const GOOGLE_BUTTON_ID = 'google-login-btn';
type Props = {
  title: string;
  onLoggedIn?: (data: any, error: Error | null) => void;
};
type Identifier = 'appleid' | 'facebook' | 'google';
const SocialGroup = (props: Props) => {
  const {
    title,
    onLoggedIn = () => {}
  } = props;
  const {
    query
  } = useRouter();
  const continueUrl = query.continue?.toString();
  const [isWebview, setIsWebview] = useState(false);
  const {
    fetch: authorize
  } = useAuthorize();
  const {
    fetch: facebookLogin
  } = useLoginViaFacebook();
  const {
    fetch: appleLogin
  } = useLoginViaApple();
  const {
    fetch: googleLogin
  } = useLoginViaGoogle();
  useEffect(() => {
    // This supposed to be initialized on soft navigation
    initGoogle();
    initApple();
    initFacebook();
    setIsWebview(checkIfWebview());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initGoogle = () => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: handleGoogleSuccess
      });
      window.google.accounts.id.renderButton(document.getElementById(GOOGLE_BUTTON_ID), {
        type: 'standart',
        click_listener: () => {
          asyncGTMClickEvent({
            label: 'log_in_with_google',
            category: 'login'
          });
        }
      });
      window.google.accounts.id.prompt();
    }
  };
  const initApple = () => {
    if (window.AppleID) {
      window.AppleID.auth.init({
        clientId: config.appleServiceId,
        redirectURI: window.location.origin,
        scope: 'name email',
        state: makeId(30),
        usePopup: true
      });
    }
  };
  const initFacebook = () => {
    if (window.FB) {
      window.FB.init({
        appId: config.fbAppId,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v9.0'
      });
    }
  };
  const onFacebookLogin = async () => {
    asyncGTMClickEvent({
      label: 'log_in_with_facebook',
      category: 'login'
    });
    const token = window.FB.getAccessToken();
    if (token) {
      const idV2 = await fingerprintV2.get();
      const {
        data: loginChallenge,
        error
      } = await authorize({
        continueUrl,
        fingerprintV2: idV2
      });
      if (loginChallenge) {
        const id = await fingerprint.get();
        const {
          data,
          error
        } = await facebookLogin({
          token,
          loginChallenge,
          fingerprint: id,
          fingerprintV2: idV2
        });
        onLoggedIn(data, error);
        if (data?.success) {
          handleLoginSuccess({
            result: data.data,
            identifier: 'facebook'
          });
        }
      } else onLoggedIn(null, error);
    } else {
      window.FB.login(async (response: any) => {
        if (response && response.status !== 'unknown' && response.authResponse?.accessToken) {
          const idV2 = await fingerprintV2.get();
          const {
            data: loginChallenge
          } = await authorize({
            continueUrl,
            fingerprintV2: idV2
          });
          if (loginChallenge) {
            const id = await fingerprint.get();
            const {
              data,
              error
            } = await facebookLogin({
              token: response?.authResponse?.accessToken,
              loginChallenge,
              fingerprint: id,
              fingerprintV2: idV2
            });
            onLoggedIn(data, error);
            if (data?.success) {
              handleLoginSuccess({
                result: data.data,
                identifier: 'facebook'
              });
            }
          }
        }
      }, {
        scope: 'email',
        return_scopes: true
      });
    }
  };
  const onAppleLogin = async () => {
    asyncGTMClickEvent({
      label: 'log_in_with_apple',
      category: 'login'
    });
    const data = await window.AppleID.auth.signIn();
    const idV2 = await fingerprintV2.get();
    const {
      data: loginChallenge,
      error
    } = await authorize({
      continueUrl,
      fingerprintV2: idV2
    });
    if (loginChallenge) {
      const id = await fingerprint.get();
      const {
        data: resData,
        error
      } = await appleLogin({
        accessToken: data.authorization.id_token,
        authorizationCode: data.authorization.code,
        userId: parseJwt(data.authorization.id_token).sub,
        loginChallenge,
        fingerprint: id,
        fingerprintV2: idV2
      });
      onLoggedIn(resData, error);
      if (resData?.success) {
        handleLoginSuccess({
          result: resData.data,
          identifier: 'appleid'
        });
      }
    } else onLoggedIn(null, error);
  };
  const handleGoogleSuccess = async ({
    credential
  }: {
    credential: string;
  }) => {
    if (credential) {
      const idV2 = await fingerprintV2.get();
      const {
        data: loginChallenge,
        error
      } = await authorize({
        continueUrl,
        fingerprintV2: idV2
      });
      if (loginChallenge) {
        const id = await fingerprint.get();
        const {
          data,
          error
        } = await googleLogin({
          token: credential,
          loginChallenge,
          fingerprint: id,
          fingerprintV2: idV2
        });
        onLoggedIn(data, error);
        if (data?.success) {
          handleLoginSuccess({
            result: data.data,
            identifier: 'google'
          });
        }
      } else onLoggedIn(null, error);
    }
  };
  const handleLoginSuccess = ({
    identifier,
    result
  }: {
    identifier: Identifier;
    result: any;
  }) => {
    // default menu mode is user
    removeCookie('currentMenuMode');
    taggingAfterLoginSuccess({
      result,
      identifier
    });
  };
  const taggingAfterLoginSuccess = async ({
    result,
    identifier
  }: {
    identifier: Identifier;
    result: any;
  }) => {
    gtmCustomDemesion({
      event: result?.profile?.login_action_type === 'LOGIN' ? 'login' : 'sign_up',
      rest: {
        method: identifier === 'appleid' ? 'apple' : identifier,
        user_profile_gender: genderToGA4Data(result?.profile?.gender),
        chotot_id: result?.profile?.account_id,
        logged_in_status: 'yes'
      }
    });
  };
  if (isWebview) return null;
  return <>
      <Script src="https://accounts.google.com/gsi/client" onLoad={initGoogle} />
      <Script src="https://connect.facebook.net/en_US/sdk.js" onLoad={initFacebook} />
      <Script src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js" onLoad={initApple} />

      <div className={additionalLoginTextCss}>
        <hr />
        <span>{title}</span>
        <hr />
      </div>
      <div className={socialButtonWrapperCss}>
        <button type="button" className={socialButtonCss} id="login-facebook-btn" onClick={onFacebookLogin}>
          <Facebook width={24} height={24} color={colors.iconInfo} />
        </button>

        <button type="button" id="login-google-btn" className={googleButtonCss}>
          <Image src="https://static.chotot.com/storage/icons/png/google.png" alt="Google" width={24} height={24} />
          <div id={GOOGLE_BUTTON_ID} />
        </button>

        <button type="button" id="login-apple-btn" className={socialButtonCss} onClick={onAppleLogin}>
          <Image src="https://static.chotot.com/storage/icons/png/apple.png" alt="Apple" width={24} height={24} />
        </button>
      </div>
    </>;
};
const additionalLoginTextCss = "a15384yj";
const socialButtonWrapperCss = "s1k9ogns";
const socialButtonCss = "sdyg7j";
const googleButtonCss = "g10u9umr";
export default SocialGroup;

require("./SocialGroup.linaria.module.css");